import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, formatDate, mensajeError } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CatalogCombobox } from '../components/qx/CatalogCombobox';
import ParteDialog from '../components/qx/ParteDialog';
// Las firmas de quirófano se hacen en la hoja de anestesia: es donde ya está
// el recuadro de firma del anestesista (2026-09-17).
import AnesthesiaSheetDialog from '../components/quirofano/AnesthesiaSheetDialog';
import { useAuth } from '../lib/auth';
import { isAdmin as isAdminRole, isManagement } from '../lib/roles';
import { Plus, ClipboardList, ClipboardCheck, Trash2, FileSpreadsheet, Edit, Paperclip, FileText, PenLine, DoorOpen, X } from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto } from '../lib/utils';
import { canonicoDeCatalogo } from '../lib/medicos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Descarga con la cookie de sesión (cp_session httpOnly → credentials).
 *
 * 2026-09-17 — `fetch` NO lanza con un 403: devuelve una respuesta normal con
 * el cuerpo del error. Sin mirar el estado, esto guardaba un
 * «registro_qx_2026-09.xlsx» que por dentro era el JSON del error, un fichero
 * que Excel no abre. Se comprueba antes de crear nada. Devuelve true solo si
 * el fichero ha llegado.
 */
async function downloadAuth(url, filename) {
  try {
    const r = await fetch(url, { credentials: 'include' });
    if (!r.ok) {
      toast.error(r.status === 403
        ? 'No tienes permiso para descargar este Excel (es de dirección).'
        : 'No se ha podido descargar el Excel.');
      return false;
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    return true;
  } catch {
    toast.error('No se ha podido descargar el Excel.');
    return false;
  }
}

const DEFAULT_FORM = () => ({
  date: new Date().toISOString().slice(0, 10), num_historia: '', patient_name: '', patient_id: null, intervention: '',
  co2: false, full_face: false, surgeon: '', anesthesiologist: '',
  instrumentista: '', circulante: '', hora_inicio: '', hora_final: '', observations: '',
  origin_clinic: '', surgery_location: '', sedacion: null
});

// Phase 27.7 — Los desplegables del formulario (cirujano, anestesista,
// instrumentista, circulante, clínica de origen y lugar) se alimentan del
// catálogo compartido /api/qx-catalogo vía <CatalogCombobox>, con opción de
// añadir nombres a mano (quedan guardados para la próxima vez). Sustituye a
// las antiguas listas fijas + SelectOrManual de la Phase 26.6.

/**
 * LAS CUATRO HORAS DE ESTA PANTALLA (19/9/2026)
 * -----------------------------------------------------
 * En el mismo registro hay DOS relojes, y no se pueden leer uno por otro:
 *
 *   · PUERTA — «Entrada quirófano» / «Salida quirófano»
 *     (`hora_entrada_quirofano` / `hora_salida_quirofano`). Las marca la
 *     auxiliar cuando el paciente ENTRA y SALE de la sala; las sella el
 *     SERVIDOR al pulsar, no se teclean. Miden la OCUPACIÓN DE SALA y NO se
 *     facturan.
 *   · CIRUGÍA — «Inicio cirugía» / «Fin cirugía» (`hora_inicio` /
 *     `hora_final`). Las sella la FIRMA del cirujano (y solo dirección las
 *     corrige). Miden la duración real de la cirugía y son las que van a la
 *     FACTURA del cirujano externo.
 *
 * Entre que el paciente entra por la puerta y el cirujano empieza a operar
 * pasan la preparación y la sedación: son momentos distintos, y esa
 * diferencia son minutos de quirófano que se facturan o no. Por eso ninguna
 * etiqueta de esta pantalla dice «inicio» o «fin» a secas. La tabla completa
 * (quién sella cada una, dónde se ve y cuál factura) está en el backend, en
 * models.py encima de SurgeryLogCreate.
 */
/** Las dos horas de PUERTA, con el nombre que se lee en pantalla. Nunca
 *  «inicio» ni «fin»: esas son las de la cirugía, y son las que se facturan. */
/** Minutos entre dos «HH:MM» de CIRUGÍA (+24 h si cruza medianoche, que es lo
 *  que hace el servidor con estas). Para las de la puerta NO vale: allí una
 *  resta negativa es una hora mal pulsada, no una madrugada. */
function minutosEntreHoras(inicio, fin) {
  if (!inicio || !fin) return null;
  const [h1, m1] = String(inicio).split(':').map(Number);
  const [h2, m2] = String(fin).split(':').map(Number);
  if ([h1, m1, h2, m2].some((n) => Number.isNaN(n))) return null;
  let d = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (d < 0) d += 24 * 60;
  return d;
}

const MOMENTOS_DE_LA_PUERTA = [
  { momento: 'entrada', campo: 'hora_entrada_quirofano', que: 'Entrada quirófano' },
  { momento: 'salida', campo: 'hora_salida_quirofano', que: 'Salida quirófano' },
];

function LogDialog({ open, onOpenChange, initial, onSaved, surgeons, parte = null }) {
  // El registro que se está editando. Empieza siendo el que se abre con el
  // lápiz, pero también lo pone el «crear al vuelo» al firmar desde el alta
  // (remate 2026-09-17): así no hay que crear, cerrar y volver a abrir.
  const [logId, setLogId] = useState(initial?.id || null);
  const isEdit = !!logId;
  const [form, setForm] = useState(DEFAULT_FORM);
  const [sheets, setSheets] = useState([]);
  const [uploadingSheet, setUploadingSheet] = useState(false);
  // Firmas de quirófano (2026-09-17): el estado lo trae el propio
  // registro (`hora_inicio_origen`, `firmas_completas`…) y se refresca al
  // firmar, sin tener que cerrar y volver a abrir.
  const [firmasOpen, setFirmasOpen] = useState(false);
  const [registro, setRegistro] = useState(null);
  const [creandoParaFirmar, setCreandoParaFirmar] = useState(false);
  // Corregir una hora que ya selló la firma del cirujano es un gesto APARTE
  // (botón «Corregir») y de dirección. Esta pantalla puede llevar abierta
  // desde antes de que el cirujano firmara en la tablet —la tabla solo se
  // recarga al montar, al cambiar de mes y al firmar—, así que «Guardar» a
  // secas no puede pisar esa hora: el servidor lo rechaza con un 409
  // (segundo remate, 2026-09-18).
  const [corrigiendoHoras, setCorrigiendoHoras] = useState(false);
  // Puerta del quirófano y «Crear parte» (2026-09-18). Lo que falta
  // para poder crear el parte lo dice el SERVIDOR (GET …/parte-requisitos), que
  // es el mismo que luego rechaza el POST: la pantalla no se inventa la regla.
  const [requisitos, setRequisitos] = useState(null);
  const [sellandoPuerta, setSellandoPuerta] = useState(null);
  const [creandoParte, setCreandoParte] = useState(false);
  // Corregir una hora de puerta mal pulsada es, como en las horas de la firma,
  // un gesto APARTE y de dirección: el servidor no la cambia sin él.
  const [corrigiendoPuerta, setCorrigiendoPuerta] = useState(null);   // 'entrada' | 'salida' | null
  const [horaDePuerta, setHoraDePuerta] = useState('');
  const { user } = useAuth();
  const puedeCorregirHoras = isManagement(user);
  // La marca de anestesia del parte (D3, 23/9/2026, diseño cerrado): quién
  // puede ponerla lo decide el servidor; aquí solo se esconden los botones a
  // quien no. «Anestesia local, sin anestesista» (y quitar la marca): solo
  // admin y dirección (y sus nombres antiguos, «medico» y «secretaria»).
  // «Lleva anestesista»: también el anestesista.
  const rolDeQuienMarca = String(user?.role || '').toLowerCase();
  const puedeMarcarLocal = ['admin', 'direccion', 'medico', 'secretaria'].includes(rolDeQuienMarca);
  const puedeMarcarAnestesista = puedeMarcarLocal || rolDeQuienMarca === 'anestesista';
  const [marcandoAnestesia, setMarcandoAnestesia] = useState(false);
  // «Anestesia local» se confirma después de ver lo que dice la hoja.
  const [confirmandoLocal, setConfirmandoLocal] = useState(false);
  // El motivo del 409 del servidor, en su sitio (no solo en un aviso que se va).
  const [errorMarca, setErrorMarca] = useState(null);

  // Phase 27.8 — Si la intervención pertenece a un parte de quirófano, el
  // equipo y la fecha se heredan del parte (no se piden aquí).
  const conParte = !!parte || !!(initial?.parte_id);

  // Phase 23.14 — CO2 zona como RadioGroup mutuamente excluyente y
  // deseleccionable. Internamente mapea a {co2, full_face} (backend
  // mantiene la misma estructura para compatibilidad):
  //   co2_zone === 'none'       → co2=false, full_face=false
  //   co2_zone === 'periocular' → co2=true,  full_face=false
  //   co2_zone === 'fullface'   → co2=true,  full_face=true
  const co2Zone = useMemo(() => {
    if (!form.co2) return 'none';
    return form.full_face ? 'fullface' : 'periocular';
  }, [form.co2, form.full_face]);

  const setCO2Zone = (zone) => {
    setForm(f => ({
      ...f,
      co2: zone !== 'none',
      full_face: zone === 'fullface',
    }));
  };

  const fetchSheets = async (logId) => {
    try {
      const r = await api.get('/api/anesthesia-sheets', { params: { surgery_log_id: logId } });
      setSheets(r.data || []);
    } catch {
      setSheets([]);
    }
  };

  useEffect(() => {
    if (open) {
      // Al crear dentro de un parte, el cirujano por defecto es el del parte
      // (editable por intervención: dos cirujanos en la misma mañana).
      setForm(initial || { ...DEFAULT_FORM(), ...(parte ? { surgeon: parte.cirujano || '' } : {}) });
      setRegistro(initial || null);
      setFirmasOpen(false);
      setCorrigiendoHoras(false);
      setLogId(initial?.id || null);
      setRequisitos(null);
      if (initial?.id) {
        fetchSheets(initial.id);
        cargarRequisitos(initial.id);
      } else {
        setSheets([]);
      }
    }
    // EL PARTE POR SU ID, NO POR EL OBJETO (24/9/2026: «el
    // anestesista firma y no se guarda, y no sale ningún mensaje»). Cada
    // firma recarga la lista y con ella los partes (`onSaved`), y el parte
    // vuelve como un objeto NUEVO: con `parte` aquí, este reinicio se
    // disparaba en mitad de «Firmas» en todo registro que está dentro de un
    // parte. Se cerraba «Firmas» sin avisar, se perdía la firma del
    // consentimiento de anestesia ya dibujada y el registro volvía a decir
    // «○ Firma anestesista» justo después de «Hoja firmada». Solo se reinicia
    // al abrir, al cambiar de registro o de parte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, parte?.id]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Estado de las firmas, tal y como lo guarda el servidor en el registro.
  const pacienteId = form.patient_id || initial?.patient_id || null;
  const anestesistaFirmada = !!registro?.firma_anestesista_at;
  // Que la hora que se VE sea una hora DECIDIDA (lleva sello y no se teclea
  // encima). Lo son las dos: la que selló la firma del cirujano y la que
  // corrigió dirección a conciencia. Esto solo miraba «firma_cirujano», así
  // que en cuanto dirección corregía una hora el campo volvía a ser
  // tecleable para cualquiera de quirófano —justo debajo del aviso de que
  // eso se factura— y se deshacía la corrección sin querer (ronda de cierre,
  // 2026-09-18). Cambiarla otra vez se pide con el botón de corregir.
  //
  // Tres cosas distintas, y mezclarlas fue justo el fallo:
  //   · …PorFirma  — la hora que se ve ES la que selló el cirujano;
  //   · …Corregida — dirección la cambió a conciencia, y esa es la que vale;
  //   · …Sellada   — cualquiera de las dos: hora decidida, campo de solo lectura.
  const inicioPorFirma = registro?.hora_inicio_origen === 'firma_cirujano';
  const finPorFirma = registro?.hora_final_origen === 'firma_cirujano';
  const inicioCorregida = !inicioPorFirma && !!registro?.hora_inicio_firmada;
  const finCorregida = !finPorFirma && !!registro?.hora_final_firmada;
  const inicioSellada = inicioPorFirma || inicioCorregida;
  const finSellada = finPorFirma || finCorregida;
  // …y que el cirujano FIRMARA son dos cosas distintas. Se derivaba del
  // origen de la hora, así que una corrección manual hacía decir al panel que
  // el cirujano no había firmado —con el registro marcado como completo al
  // lado—. Firmar es un hecho de la firma: lo dice `*_firmada_at`, que solo
  // escribe el servidor al firmar (segundo remate, 2026-09-18).
  // Ojo al derivarlo de `*Sellada`: desde hoy eso incluye «corregida a mano»,
  // que no es una firma. Aquí se pregunta por el origen de la FIRMA a secas.
  const inicioFirmado = inicioPorFirma
    || !!registro?.hora_inicio_firmada_at || !!registro?.hora_inicio_firmada;
  const finFirmado = finPorFirma
    || !!registro?.hora_final_firmada_at || !!registro?.hora_final_firmada;
  // La hora que dijo la firma, esté puesta o corregida a mano.
  const horaFirmadaInicio = inicioPorFirma ? registro?.hora_inicio : registro?.hora_inicio_firmada;
  const horaFirmadaFin = finPorFirma ? registro?.hora_final : registro?.hora_final_firmada;
  // Lo imprescindible para poder crear el registro al vuelo desde las firmas
  // (son los dos campos obligatorios del formulario).
  const faltanDatosMinimos = !(form.patient_name || '').trim() || !(form.intervention || '').trim();
  // Las horas de la PUERTA (ocupación de sala). Salen siempre del registro que
  // devuelve el servidor, nunca del formulario: no se teclean.
  const entradaPuerta = registro?.hora_entrada_quirofano || '';
  const salidaPuerta = registro?.hora_salida_quirofano || '';
  const ocupacionMinutos = registro?.ocupacion_minutos;
  // La duración de la CIRUGÍA: la que manda el servidor (de firma a firma, o
  // la corregida por dirección) y, si no la hay, la que sale de las horas de
  // cirugía del formulario. Nunca la ocupación de sala: son dos números
  // distintos y solo este se factura.
  const duracionCirugia = useMemo(() => {
    if (registro?.duracion_minutos !== null && registro?.duracion_minutos !== undefined) {
      return registro.duracion_minutos;
    }
    return minutosEntreHoras(form.hora_inicio, form.hora_final);
  }, [registro?.duracion_minutos, form.hora_inicio, form.hora_final]);
  const parteCreadoAt = registro?.parte_creado_at || requisitos?.parte_creado_at || null;
  // Lo que el SERVIDOR dice que falta. Mientras no conteste (o si contesta
  // algo que no se entiende) el botón sigue apagado y la pantalla lo dice: no
  // se da por bueno lo que no se sabe.
  const faltaParaElParte = Array.isArray(requisitos?.faltan) ? requisitos.faltan : null;
  const parteListo = requisitos?.listo === true;

  const handleUploadSheet = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!logId) {
      toast.error('Guarda primero el registro y vuelve a abrirlo para subir la hoja.');
      return;
    }
    const pid = form.patient_id || initial?.patient_id;
    if (!pid) {
      toast.error('Este registro no está vinculado a un paciente con ID. Edita primero el paciente.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('patient_id', pid);
    fd.append('surgery_date', form.date);
    fd.append('surgery_log_id', logId);
    setUploadingSheet(true);
    try {
      await api.post('/api/anesthesia-sheets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Hoja de anestesia adjuntada');
      await fetchSheets(logId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al subir la hoja');
    } finally {
      setUploadingSheet(false);
    }
  };

  const handleDeleteSheet = async (sheetId) => {
    if (!window.confirm('¿Eliminar esta hoja de anestesia?')) return;
    try {
      await api.delete(`/api/anesthesia-sheets/${sheetId}`);
      toast.success('Hoja eliminada');
      await fetchSheets(logId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  /**
   * El registro donde se sella lo que pase ahora, creándolo al vuelo si la
   * auxiliar todavía no le ha dado a guardar.
   *
   * Dirección (18/9/2026): «rellena los datos del formulario pero no lo guarda,
   * el formulario se queda abierto». Aun así la hora de la puerta y las firmas
   * las sella el SERVIDOR sobre un registro que tiene que existir, así que la
   * primera de esas acciones lo crea y el diálogo se queda en él. Lo escrito
   * no se pierde y nadie tiene que acordarse de guardar antes.
   */
  const asegurarRegistro = async () => {
    if (logId) return { id: logId, doc: registro };
    const payload = parte ? { ...form, parte_id: parte.id, date: parte.date } : form;
    const { data } = await api.post('/api/surgery-log', payload);
    setLogId(data.id);
    setForm((f) => ({ ...f, ...data }));
    setRegistro(data);
    onSaved && onSaved();
    return { id: data.id, doc: data };
  };

  /** Lo que falta para crear el parte, según el servidor. */
  /**
   * La marca explícita de la anestesia de esta intervención (23/9/2026,
   * DECISIÓN D3 de dirección: una cirugía menor no lleva anestesista). Para los casos
   * dudosos: «Lleva anestesista» o «Anestesia local, sin anestesista»; `null`
   * la deshace. El servidor guarda quién y cuándo, y decide qué pesa: la
   * local nunca quita la firma del anestesista, un anestesista elegido ni la
   * sedación marcada. Después se vuelve a preguntar qué falta.
   */
  const marcarAnestesia = async (marca, extra = {}) => {
    if (!logId) return;
    setMarcandoAnestesia(true);
    setErrorMarca(null);
    try {
      await api.put(`/api/surgery-log/${logId}/anestesia`, { marca, ...extra });
      setConfirmandoLocal(false);
      await cargarRequisitos(logId);
      toast.success(marca === 'anestesista' ? 'Marcado: lleva anestesista'
        : marca === 'local' ? 'Marcado: anestesia local, sin anestesista'
          : 'Marca de anestesia quitada');
    } catch (e) {
      // El 409 dice POR QUÉ no se puede (no hay cita de quirófano, otra cita
      // no es de cirugía menor, la hoja tiene anestesista…): se queda escrito.
      const motivo = mensajeError(e, 'No se pudo guardar la marca de anestesia');
      setErrorMarca(motivo);
      toast.error(motivo);
    } finally {
      setMarcandoAnestesia(false);
    }
  };

  const cargarRequisitos = async (id) => {
    if (!id) { setRequisitos(null); return; }
    try {
      const r = await api.get(`/api/surgery-log/${id}/parte-requisitos`);
      setRequisitos(r.data);
    } catch {
      // Un fallo al preguntar NO se enseña como «ya se puede»: el botón se
      // queda apagado y dice por qué. Un error no es un dato.
      setRequisitos({ listo: false, faltan: [{
        clave: 'error',
        texto: 'la comprobación del servidor: no se ha podido consultar qué falta (vuelve a abrir el registro)',
      }] });
    }
  };

  /**
   * «La auxiliar introduce la hora de inicio cuando el paciente ENTRA POR LA
   * PUERTA del quirófano» y la de fin cuando SALE (18/9/2026). La
   * hora la pone el servidor al pulsar: no se teclea, porque una hora tecleada
   * es la hora «no siempre rigurosa» de la que venimos. No toca las de la
   * cirugía, que son las de las firmas.
   */
  const marcarPuerta = async (momento) => {
    if (!logId && faltanDatosMinimos) {
      toast.error('Pon el paciente y la intervención antes de marcar la puerta.');
      return;
    }
    setSellandoPuerta(momento);
    try {
      const { id } = await asegurarRegistro();
      const { data } = await api.post(`/api/surgery-log/${id}/puerta`, { momento });
      setRegistro((r) => ({ ...(r || {}), ...data }));
      toast.success(momento === 'entrada'
        ? `Entrada al quirófano sellada a las ${data.hora_entrada_quirofano}`
        : `Salida del quirófano sellada a las ${data.hora_salida_quirofano}`);
      onSaved && onSaved();
      await cargarRequisitos(id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se ha podido sellar la hora de la puerta');
    } finally {
      setSellandoPuerta(null);
    }
  };

  /**
   * La salida para una hora de puerta mal pulsada (19/9/2026): se
   * pulsa «SALE por la puerta» sin querer y el registro se queda con una
   * ocupación imposible que no puede arreglar nadie —una hora sellada no se
   * vuelve a sellar y el formulario no la escribe—.
   *
   * El gesto viaja en la petición (`corregir_hora_puerta`) y es de dirección,
   * igual que corregir una hora sellada por la firma del cirujano. Quien manda
   * es el SERVIDOR: aquí solo se pide.
   */
  const corregirHoraDePuerta = async (momento) => {
    if (!logId) return;
    const hora = (horaDePuerta || '').trim();
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(hora)) {
      toast.error('Pon la hora corregida como HH:MM.');
      return;
    }
    setSellandoPuerta(momento);
    try {
      const { data } = await api.post(`/api/surgery-log/${logId}/puerta/corregir`,
        { momento, hora, corregir_hora_puerta: true });
      setRegistro((r) => ({ ...(r || {}), ...data }));
      setCorrigiendoPuerta(null);
      setHoraDePuerta('');
      toast.success(`Hora de puerta corregida a las ${hora}`);
      onSaved && onSaved();
      await cargarRequisitos(logId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se ha podido corregir la hora de la puerta');
    } finally {
      setSellandoPuerta(null);
    }
  };

  /**
   * «Crear parte»: lo habilita el servidor, no esta pantalla. Si dice que
   * falta algo (409), se pinta lo que falta y no se canta ningún parte creado.
   */
  const crearParte = async () => {
    if (!logId) return;
    setCreandoParte(true);
    try {
      const { data } = await api.post(`/api/surgery-log/${logId}/parte`);
      setRegistro((r) => ({ ...(r || {}), ...data }));
      toast.success('Parte creado');
      onSaved && onSaved();
      await cargarRequisitos(logId);
    } catch (err) {
      const detalle = err?.response?.data?.detail;
      toast.error(typeof detalle === 'string'
        ? detalle
        : (detalle?.mensaje || 'No se ha podido crear el parte'));
      if (detalle && Array.isArray(detalle.faltan)) {
        setRequisitos({ listo: false, faltan: detalle.faltan });
      } else {
        await cargarRequisitos(logId);
      }
    } finally {
      setCreandoParte(false);
    }
  };

  /**
   * «Al crear un nuevo registro quirúrgico, debajo de la hoja de anestesia
   * deben aparecer dos campos de firma» (2026-09-17). En el alta el
   * botón salía apagado con un «crea el registro y vuelve a abrirlo»: ahora
   * pulsarlo GUARDA el registro al vuelo y el diálogo se queda en ÉL, en modo
   * edición, con la hoja abierta (remate 2026-09-17).
   */
  const abrirFirmas = async () => {
    if (logId) {
      setFirmasOpen(true);
      return;
    }
    setCreandoParaFirmar(true);
    try {
      const { doc: data } = await asegurarRegistro();
      if (data.patient_id) {
        toast.success('Registro creado: ya se puede firmar');
        setFirmasOpen(true);
      } else {
        // La hoja de anestesia es del PACIENTE: sin ficha vinculada no hay
        // hoja que firmar, pero lo escrito ya está guardado.
        toast.info('Registro creado. Para firmar la hoja hay que vincularlo a la ficha del paciente.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se ha podido crear el registro');
    } finally {
      setCreandoParaFirmar(false);
    }
  };

  /**
   * Cuando el servidor rechaza el guardado porque esta pantalla va más vieja
   * que la firma (409), se vuelve a pedir el registro: así se ve la hora que
   * selló la firma en vez de dejar al usuario discutiendo con una hora que ya
   * no existe. Lo que estuviera escribiendo (observaciones…) no se le borra.
   */
  const recargarRegistro = async () => {
    if (!logId) return;
    try {
      const r = await api.get('/api/surgery-log', { params: { month: (form.date || '').slice(0, 7) } });
      const fresco = (r.data || []).find((x) => x.id === logId);
      if (!fresco) return;
      setRegistro(fresco);
      setForm((f) => ({ ...f, hora_inicio: fresco.hora_inicio || '', hora_final: fresco.hora_final || '' }));
      setCorrigiendoHoras(false);
      onSaved && onSaved();
    } catch {
      // Si tampoco se puede recargar, el mensaje del servidor ya lo explica.
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Con parte: el cirujano puede diferir del parte (override por
      // intervención); el resto del equipo lo hereda/propaga el parte.
      const surgeonOverride = parte ? (form.surgeon || '') !== (parte.cirujano || '') : undefined;
      // La marca de anestesia y su historial NO viajan con «Guardar» (D3,
      // 23/9/2026): los escribe solo su botón, y la copia de esta pantalla
      // puede ser de antes de que alguien marcara «Lleva anestesista».
      const { anestesia_marcada: _m, anestesia_marcada_historial: _h, ...formSinMarca } = form;
      if (isEdit) {
        let payload = surgeonOverride === undefined
          ? formSinMarca : { ...formSinMarca, surgeon_override: surgeonOverride };
        // El gesto explícito del botón «Corregir». Solo va cuando alguien ha
        // decidido cambiar una hora sellada: un formulario abierto antes de
        // la firma no lo manda nunca, y por eso el servidor lo puede exigir.
        if (corrigiendoHoras) payload = { ...payload, corregir_hora_firmada: true };
        await api.put(`/api/surgery-log/${logId}`, payload);
        toast.success('Actualizado');
      } else {
        // El backend copia el equipo y la fecha del parte al crear.
        const payload = parte ? { ...formSinMarca, parte_id: parte.id, date: parte.date } : formSinMarca;
        await api.post('/api/surgery-log', payload);
        toast.success('Creado');
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
      // 409 = la hora la selló la firma después de abrir esta pantalla.
      if (err?.response?.status === 409) await recargarRegistro();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar registro' : 'Nuevo registro quirúrgico'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Phase 27.8 — con parte: el equipo y la fecha vienen del parte */}
          {conParte && (
            <div
              className="md:col-span-3 rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] px-3 py-2 text-sm text-[#13231E]"
              data-testid="sx-log-parte-info"
            >
              {parte ? (
                <>
                  Parte del <span className="font-semibold">{formatDate(parte.date)}</span> ·{' '}
                  <span className="font-semibold">{parte.cirujano}</span>
                  {parte.lugar ? <> · {parte.lugar}</> : null}
                  {parte.anestesista ? <> · Anestesista: {parte.anestesista}</> : null}
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    El equipo se hereda del parte; se cambia editando el parte.
                  </span>
                </>
              ) : (
                <>El equipo de esta intervención lo define su parte de quirófano.</>
              )}
            </div>
          )}
          {!conParte && (
            <div><Label>Fecha</Label><Input type="date" required value={form.date} onChange={(e) => setF('date', e.target.value)} data-testid="sx-log-date" /></div>
          )}
          <div><Label>Nº historia</Label><Input value={form.num_historia} onChange={(e) => setF('num_historia', e.target.value)} /></div>
          <div className="md:col-span-1"><Label>Paciente</Label><Input required value={form.patient_name} onChange={(e) => setF('patient_name', e.target.value)} data-testid="sx-log-patient" /></div>
          <div className="md:col-span-3"><Label>Intervención</Label><Input required value={form.intervention} onChange={(e) => setF('intervention', e.target.value)} placeholder="ej.: nombre de la intervención" data-testid="sx-log-intervention" /></div>

          {/* Phase 23.14 — CO2 zona (mutuamente excluyente, deseleccionable) */}
          <div className="md:col-span-2">
            <Label>Láser CO2</Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="sx-log-co2-group">
              <Button
                type="button"
                size="sm"
                variant={co2Zone === 'periocular' ? 'default' : 'outline'}
                onClick={() => setCO2Zone(co2Zone === 'periocular' ? 'none' : 'periocular')}
                className={co2Zone === 'periocular' ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F]' : ''}
                data-testid="sx-log-co2-periocular"
              >
                CO2 periocular
              </Button>
              <Button
                type="button"
                size="sm"
                variant={co2Zone === 'fullface' ? 'default' : 'outline'}
                onClick={() => setCO2Zone(co2Zone === 'fullface' ? 'none' : 'fullface')}
                className={co2Zone === 'fullface' ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F]' : ''}
                data-testid="sx-log-co2-fullface"
              >
                CO2 full face
              </Button>
              {co2Zone !== 'none' && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setCO2Zone('none')} className="text-xs text-muted-foreground">
                  <X className="w-3 h-3 mr-1" /> Limpiar
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Si la cirugía no incluye CO2, deja ambos sin marcar.
            </p>
          </div>

          {/* Phase 27.8 — El cirujano SÍ es editable por intervención (dos
              cirujanos en la misma mañana); el resto del equipo lo define el
              parte. En registros sin parte se piden todos los campos. */}
          <div>
            <Label>Cirujano</Label>
            <CatalogCombobox
              tipo="cirujano"
              value={form.surgeon}
              onChange={(nombre, entry) => {
                setF('surgeon', nombre);
                if (!conParte && entry?.clinica) setF('origin_clinic', entry.clinica);
              }}
              placeholder="Selecciona cirujano"
              dataTestid="qx-cirujano"
            />
            {conParte && parte && (form.surgeon || '') !== (parte.cirujano || '') && (
              <p className="text-[11px] text-amber-700 mt-1">
                Distinto al del parte ({parte.cirujano}) — se guardará solo en esta intervención.
              </p>
            )}
          </div>

          {!conParte && (
          <div>
            <Label>Anestesiólogo</Label>
            <CatalogCombobox
              tipo="anestesista"
              value={form.anesthesiologist}
              onChange={(nombre) => setF('anesthesiologist', nombre)}
              placeholder="Selecciona anestesista"
              allowEmpty
              dataTestid="qx-anestesista"
            />
          </div>
          )}

          {/* Phase 27.7 — ¿Con sedación? (null = sin indicar) */}
          <div>
            <Label>¿Con sedación?</Label>
            <Select
              value={form.sedacion === true ? 'si' : form.sedacion === false ? 'no' : 'na'}
              onValueChange={(v) => setF('sedacion', v === 'si' ? true : v === 'no' ? false : null)}
            >
              <SelectTrigger data-testid="qx-sedacion">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="na">—</SelectItem>
                <SelectItem value="si">Sí</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!conParte && (
          <div>
            <Label>Clínica de origen</Label>
            <CatalogCombobox
              tipo="clinica_origen"
              value={form.origin_clinic}
              onChange={(nombre) => setF('origin_clinic', nombre)}
              placeholder="Selecciona clínica"
              allowEmpty
              dataTestid="qx-clinica-origen"
            />
          </div>
          )}

          {!conParte && (
          <div>
            <Label>Lugar de la cirugía</Label>
            <CatalogCombobox
              tipo="lugar"
              value={form.surgery_location}
              onChange={(nombre) => setF('surgery_location', nombre)}
              placeholder="Selecciona lugar"
              allowEmpty
              dataTestid="qx-lugar"
            />
          </div>
          )}

          {!conParte && (
          <div>
            {/* 2026-09-09 (punto 3): el instrumentista sale del
                catálogo como los demás. Antes era una lista fija con
                «enfermería» y «Externo», las entradas genéricas que pidió
                quitar; los registros antiguos que las llevan no se tocan. */}
            <Label>Instrumentista</Label>
            <CatalogCombobox
              tipo="instrumentista"
              value={form.instrumentista}
              onChange={(nombre) => setF('instrumentista', nombre)}
              placeholder="Selecciona instrumentista"
              allowEmpty
              dataTestid="qx-instrumentista"
            />
          </div>
          )}
          {!conParte && (
          <div>
            <Label>Circulante</Label>
            <CatalogCombobox
              tipo="circulante"
              value={form.circulante}
              onChange={(nombre) => setF('circulante', nombre)}
              placeholder="Selecciona circulante"
              allowEmpty
              dataTestid="qx-circulante"
            />
          </div>
          )}
          {/* ---- PUERTA DEL QUIRÓFANO · ocupación de sala ----
              18/9/2026: la auxiliar marca cuándo el paciente ENTRA
              y cuándo SALE por la puerta. La hora la pone el SERVIDOR al
              pulsar —no se teclea— y NO es la de la cirugía: esa la sella la
              firma del cirujano (aquí debajo) y es la que se factura. */}
          <div className="md:col-span-3 border-t border-border pt-3 mt-2" data-testid="sx-log-puerta">
            <Label className="flex items-center gap-1.5">
              <DoorOpen className="w-4 h-4" /> Puerta del quirófano · ocupación de sala
            </Label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {entradaPuerta ? (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[rgba(46,110,94,0.10)] text-xs text-[#13231E]"
                  data-testid="sx-log-puerta-entrada"
                >
                  ✓ Entrada quirófano {entradaPuerta}
                  {registro?.hora_entrada_quirofano_por_name ? ` · ${registro.hora_entrada_quirofano_por_name}` : ''}
                </span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={sellandoPuerta !== null}
                  onClick={() => marcarPuerta('entrada')}
                  data-testid="sx-log-puerta-marcar-entrada"
                >
                  <DoorOpen className="w-3.5 h-3.5 mr-1" /> El paciente ENTRA por la puerta
                </Button>
              )}
              {salidaPuerta ? (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[rgba(46,110,94,0.10)] text-xs text-[#13231E]"
                  data-testid="sx-log-puerta-salida"
                >
                  ✓ Salida quirófano {salidaPuerta}
                  {registro?.hora_salida_quirofano_por_name ? ` · ${registro.hora_salida_quirofano_por_name}` : ''}
                </span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={sellandoPuerta !== null}
                  onClick={() => marcarPuerta('salida')}
                  data-testid="sx-log-puerta-marcar-salida"
                >
                  <DoorOpen className="w-3.5 h-3.5 mr-1" /> El paciente SALE por la puerta
                </Button>
              )}
              {ocupacionMinutos !== null && ocupacionMinutos !== undefined && (
                <span className="text-xs text-muted-foreground" data-testid="sx-log-ocupacion">
                  Ocupación de sala: {ocupacionMinutos} min (no se factura)
                </span>
              )}
            </div>
            {/* Lo que dice el SERVIDOR cuando las dos horas no cuadran (una se
                pulsó sin querer). Antes se restaban los «HH:MM» y una salida
                pulsada antes de la entrada dejaba «1435 min» pintados como un
                dato: ahora no hay número y se dice qué pasa. */}
            {registro?.ocupacion_aviso && (
              <p className="text-xs text-amber-700 mt-1" data-testid="sx-log-ocupacion-aviso">
                {registro.ocupacion_aviso}
              </p>
            )}
            {/* Lo que corrigió dirección: la hora que selló el servidor no se
                borra, se enseña al lado (igual que con las de la firma). */}
            {MOMENTOS_DE_LA_PUERTA.map(({ momento, campo, que }) => (
              registro?.[`${campo}_sellada`] ? (
                <p
                  key={`nota-${momento}`}
                  className="text-[11px] text-amber-700 mt-1"
                  data-testid={`sx-log-puerta-${momento}-nota`}
                >
                  {que} la corrigió a mano
                  {registro[`${campo}_corregida_por_name`] ? ` ${registro[`${campo}_corregida_por_name`]}` : ''};
                  {' '}el servidor había sellado {registro[`${campo}_sellada`]}.
                </p>
              ) : null
            ))}
            {/* LA SALIDA PARA UNA HORA MAL PULSADA (19/9/2026). Hasta
                hoy no la tenía nadie: una hora de puerta sellada no se vuelve a
                sellar y el formulario no la escribe, así que un «SALE» pulsado
                sin querer dejaba el registro —y el parte que cuelga de él—
                colgado para siempre. Mismo patrón que las horas de la firma:
                gesto explícito y dirección; el candado está en el servidor. */}
            {puedeCorregirHoras && (entradaPuerta || salidaPuerta) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {MOMENTOS_DE_LA_PUERTA.map(({ momento, campo, que }) => (
                  registro?.[campo] ? (
                    corrigiendoPuerta === momento ? (
                      <span key={momento} className="inline-flex items-center gap-1.5 text-xs">
                        {que}:
                        <Input
                          type="time"
                          value={horaDePuerta}
                          onChange={(e) => setHoraDePuerta(e.target.value)}
                          className="h-8 w-28"
                          data-testid={`sx-log-puerta-hora-${momento}`}
                        />
                        <Button
                          type="button" size="sm"
                          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                          disabled={sellandoPuerta !== null}
                          onClick={() => corregirHoraDePuerta(momento)}
                          data-testid={`sx-log-puerta-guardar-${momento}`}
                        >
                          Guardar la hora corregida
                        </Button>
                        <Button
                          type="button" size="sm" variant="ghost"
                          onClick={() => setCorrigiendoPuerta(null)}
                          data-testid={`sx-log-puerta-cancelar-${momento}`}
                        >
                          Cancelar
                        </Button>
                      </span>
                    ) : (
                      <Button
                        key={momento}
                        type="button" size="sm" variant="outline"
                        onClick={() => { setCorrigiendoPuerta(momento); setHoraDePuerta(registro[campo] || ''); }}
                        data-testid={`sx-log-puerta-corregir-${momento}`}
                      >
                        Corregir {que.toLowerCase()}
                      </Button>
                    )
                  ) : null
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              La hora la sella el servidor al pulsar, con el reloj de la clínica: no se teclea.
              Mide la OCUPACIÓN DE LA SALA y no se factura: lo que se factura al cirujano
              externo son las horas de la CIRUGÍA, las de sus firmas.
              {!puedeCorregirHoras && ' Si una se pulsó sin querer, solo dirección la corrige.'}
            </p>
          </div>

          {/* Una hora que ha sellado la firma del cirujano NO se teclea
              encima: en la tabla lleva la pluma de «hora sellada por la firma
              del cirujano» y es la que va a la factura del cirujano externo.
              Se ve, pero en solo lectura. Corregirla es un gesto aparte —el
              botón de al lado, solo dirección— y lo comprueba el SERVIDOR:
              esta pantalla puede llevar abierta desde antes de la firma. Si
              se corrige, al lado queda escrito lo que decía la firma. */}
          <div>
            {/* «Inicio cirugía», no «hora inicio»: desde el 19/9 hay otra hora
                de inicio en este mismo registro —la de la puerta— y son cosas
                distintas. Esta es la de la FIRMA, y es la que se factura. */}
            <Label>Inicio cirugía</Label>
            <Input
              type="time" value={form.hora_inicio}
              onChange={(e) => setF('hora_inicio', e.target.value)}
              readOnly={inicioSellada && !corrigiendoHoras}
              title={inicioSellada ? 'Hora sellada por la firma del cirujano' : undefined}
              className={inicioSellada && !corrigiendoHoras ? 'bg-muted' : undefined}
              data-testid="sx-log-hora-inicio"
            />
            {inicioPorFirma && <p className="text-[11px] text-muted-foreground mt-1">Sellada por la firma del cirujano.</p>}
            {inicioCorregida && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="sx-log-hora-inicio-nota">
                Corregida a mano; la firma decía {registro.hora_inicio_firmada}
                {registro.hora_inicio_corregida_por_name ? ` (${registro.hora_inicio_corregida_por_name})` : ''}.
                {' '}Se factura la corregida.
              </p>
            )}
          </div>
          <div>
            <Label>Fin cirugía</Label>
            <Input
              type="time" value={form.hora_final}
              onChange={(e) => setF('hora_final', e.target.value)}
              readOnly={finSellada && !corrigiendoHoras}
              title={finSellada ? 'Hora sellada por la firma del cirujano' : undefined}
              className={finSellada && !corrigiendoHoras ? 'bg-muted' : undefined}
              data-testid="sx-log-hora-final"
            />
            {finPorFirma && <p className="text-[11px] text-muted-foreground mt-1">Sellada por la firma del cirujano.</p>}
            {/* LA DURACIÓN QUE SE FACTURA (19/9/2026). El único
                número de minutos que se veía en todo el Registro era la
                ocupación de sala —la que NO se factura—; esta, que es la que
                va a la factura del cirujano externo, no se enseñaba en ninguna
                parte. Dos números de la misma casa y solo el equivocado a la
                vista. Va aquí, al lado de SUS horas. */}
            {duracionCirugia !== null && duracionCirugia !== undefined && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="sx-log-duracion-cirugia">
                Duración de la cirugía: <span className="font-semibold text-[#13231E]">{duracionCirugia} min</span>
                {' '}— es la que se factura al cirujano externo.
              </p>
            )}
            {finCorregida && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="sx-log-hora-final-nota">
                Corregida a mano; la firma decía {registro.hora_final_firmada}
                {registro.hora_final_corregida_por_name ? ` (${registro.hora_final_corregida_por_name})` : ''}.
                {' '}Se factura la corregida.
              </p>
            )}
          </div>
          {(inicioSellada || finSellada) && (
            <div className="flex flex-col justify-end pb-1">
              {!puedeCorregirHoras ? (
                <p className="text-[11px] text-muted-foreground">
                  Una hora sellada por la firma solo la corrige dirección: es la que se
                  factura al cirujano externo.
                </p>
              ) : corrigiendoHoras ? (
                /* Tercer remate (2026-09-18): corregir una hora
                   sellada MUEVE el importe del cirujano externo, así que el
                   aviso tiene que decirlo. Antes solo prometía el rastro —«se
                   conservará al lado»— mientras la factura seguía saliendo con
                   la hora de la firma, y dirección se quedaba convencida de
                   haberlo arreglado. */
                <p className="text-[11px] text-amber-700" data-testid="sx-log-corrigiendo">
                  Corrigiendo una hora sellada: al guardar quedará escrito que la corregiste
                  tú y la hora de la firma se conservará al lado. La corregida pasa a ser la
                  buena: con ella se calculan la duración y el importe del cirujano externo.
                </p>
              ) : (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setCorrigiendoHoras(true)}
                    data-testid="sx-log-corregir-hora"
                  >
                    Corregir hora sellada
                  </Button>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Cambia también la duración y el importe del informe de externos.
                  </p>
                </>
              )}
            </div>
          )}
          <div className="md:col-span-3"><Label>Observaciones</Label><Textarea value={form.observations} onChange={(e) => setF('observations', e.target.value)} /></div>

          {/* Phase 23.14 / Phase 24.B1 — Hoja de Anestesia.
              Antes solo aparecía en modo edición; ahora la mostramos siempre
              para que el usuario sepa que existe. Si no hay registro guardado
              todavía, mostramos un aviso amable y al pulsar "Adjuntar" se
              muestra el toast explicativo (lógica en handleUploadSheet). */}
          <div className="md:col-span-3 border-t border-border pt-3 mt-2">
            <Label className="flex items-center gap-1.5">
              <Paperclip className="w-4 h-4" /> Hoja de Anestesia
            </Label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {sheets.map(s => (
                <div key={s.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[rgba(46,110,94,0.08)] border border-[rgba(46,110,94,0.3)] rounded-md text-xs">
                  <FileText className="w-3.5 h-3.5 text-[#13231E]" />
                  <a
                    href={`${BACKEND_URL}/api/anesthesia-sheets/${s.id}/file`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline text-[#13231E]"
                    data-testid="sx-log-sheet-link"
                  >
                    {s.file_name}
                  </a>
                  <span className="text-muted-foreground">· {Math.round(s.size_bytes / 1024)} KB</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDeleteSheet(s.id)}
                    className="h-5 w-5 ml-1 text-destructive"
                    data-testid="sx-log-sheet-delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <label className={`inline-flex items-center gap-1 px-3 py-1.5 border-2 border-dashed rounded-md text-xs cursor-pointer ${isEdit ? 'border-[#2E6E5E]/60 hover:bg-[#2E6E5E]/5' : 'border-muted-foreground/40 opacity-70'}`}>
                <Paperclip className="w-3.5 h-3.5" />
                <span>{uploadingSheet ? 'Subiendo…' : (isEdit ? 'Adjuntar PDF/foto' : 'Adjuntar (guarda primero)')}</span>
                <input
                  type="file"
                  accept="application/pdf,image/*,.heic,.heif"
                  className="hidden"
                  onChange={handleUploadSheet}
                  disabled={uploadingSheet}
                  data-testid="sx-log-sheet-upload"
                />
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isEdit
                ? 'Sube el papel firmado por el anestesiólogo (PDF o foto del impreso). Máx 10 MB por archivo.'
                : 'Tras crear el registro, vuelve a abrirlo (lápiz de editar) para adjuntar el papel firmado por el anestesiólogo.'}
            </p>
          </div>

          {/* Dirección (2026-09-17): «al crear un nuevo registro quirúrgico,
              debajo de la hoja de anestesia deben aparecer dos campos de
              firma: Firma anestesista y al lado Firma cirujano». Los dos
              recuadros están en la hoja de anestesia —es donde ya se firma—;
              aquí se ve cómo van y se abre para firmar.

              Lo importante: la falta de firmas (o de hoja) NO impide guardar
              el registro. Solo impide darlo por COMPLETO. */}
          <div className="md:col-span-3 border-t border-border pt-3 mt-2" data-testid="sx-log-firmas">
            <Label className="flex items-center gap-1.5">
              <PenLine className="w-4 h-4" /> Firmas de quirófano
            </Label>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {/* Sin anestesia (D3, 23/9/2026) no hay firma de anestesista que
                  esperar: no se pinta en ámbar como si faltara. Lo dice el
                  servidor (`lleva_anestesia: false`), no esta pantalla. */}
              <span
                className={anestesistaFirmada ? 'text-emerald-700'
                  : requisitos?.lleva_anestesia === false ? 'text-muted-foreground' : 'text-amber-700'}
                data-testid="sx-log-firma-anestesista"
              >
                {anestesistaFirmada ? '✓ Firma anestesista'
                  : requisitos?.lleva_anestesia === false ? '— Sin anestesista (anestesia local)'
                    : '○ Firma anestesista'}
              </span>
              <span className={inicioFirmado ? 'text-emerald-700' : 'text-amber-700'} data-testid="sx-log-firma-inicio">
                {inicioFirmado
                  ? `✓ Cirujano · inicio ${horaFirmadaInicio || ''}${inicioCorregida ? ' (corregida a mano)' : ''}`
                  : '○ Cirujano · inicio'}
              </span>
              <span className={finFirmado ? 'text-emerald-700' : 'text-amber-700'} data-testid="sx-log-firma-fin">
                {finFirmado
                  ? `✓ Cirujano · fin ${horaFirmadaFin || ''}${finCorregida ? ' (corregida a mano)' : ''}`
                  : '○ Cirujano · fin'}
              </span>
              <span
                className={registro?.firmas_completas
                  ? 'px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.12)] text-[#13231E]'
                  : 'px-1.5 py-0.5 rounded bg-[rgba(180,120,0,0.10)] text-amber-800'}
                data-testid="sx-log-firmas-estado"
              >
                {registro?.firmas_completas ? 'Registro completo' : 'Pendiente de firmas'}
              </span>
            </div>
            <div className="mt-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={creandoParaFirmar || (isEdit ? !pacienteId : faltanDatosMinimos)}
                onClick={abrirFirmas}
                data-testid="sx-log-firmar"
              >
                <PenLine className="w-3.5 h-3.5 mr-1" />
                {isEdit ? 'Abrir hoja de anestesia y firmar' : 'Crear registro y firmar'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {!isEdit
                ? (faltanDatosMinimos
                  ? 'Pon el paciente y la intervención: el registro se crea al pulsar firmar y te quedas en él.'
                  : 'Al pulsar firmar se guarda el registro y se abre su hoja de anestesia, sin salir de aquí: la hora se sella al firmar, no hay que apuntarla.')
                : !pacienteId
                  ? 'Este registro no está vinculado a la ficha de un paciente: vincúlalo para poder firmar la hoja.'
                  : 'El cirujano firma antes de empezar (sella la hora de inicio) y al terminar (sella la hora final); el fin, después de que firme el anestesista.'}
            </p>
          </div>

          {/* ---- CREAR PARTE ----
              «Una vez completado todo (horas registradas, hoja de anestesia
              firmada, consentimiento de anestesia firmado) se habilita el
              botón de Crear parte» (18/9/2026).

              Quien lo habilita de verdad es el SERVIDOR: esta pantalla puede
              llevar abierta desde antes de que el anestesista firmara, así que
              lo que falta se le pregunta a él y él mismo rechaza el POST si
              alguien insiste. Y lo que falta se DICE: un botón gris sin
              explicación hace perder media mañana (19/9/2026). */}
          <div className="md:col-span-3 border-t border-border pt-3 mt-2" data-testid="sx-log-parte">
            <Label className="flex items-center gap-1.5">
              <ClipboardCheck className="w-4 h-4" /> Parte de esta intervención
            </Label>
            <div className="mt-2">
              <Button
                type="button"
                size="sm"
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                disabled={!logId || creandoParte || !!parteCreadoAt || !parteListo}
                onClick={crearParte}
                data-testid="sx-log-crear-parte"
              >
                <ClipboardCheck className="w-3.5 h-3.5 mr-1" />
                {parteCreadoAt ? 'Parte creado' : 'Crear parte'}
              </Button>
            </div>
            {parteCreadoAt ? (
              <p className="text-xs text-emerald-700 mt-1" data-testid="sx-log-parte-creado">
                Parte creado el {formatDate(String(parteCreadoAt).slice(0, 10))}
                {registro?.parte_creado_por_name ? ` por ${registro.parte_creado_por_name}` : ''}.
              </p>
            ) : !logId ? (
              <p className="text-xs text-muted-foreground mt-1" data-testid="sx-log-parte-falta">
                El parte se crea al final: falta la hora de entrada del paciente al quirófano, la
                hora de salida y, si la intervención lleva anestesia, la hoja de anestesia firmada
                por el anestesista y el consentimiento de anestesia firmado por el paciente.
              </p>
            ) : parteListo ? (
              /* D3 de dirección (22/9/2026): una intervención con anestesia local no
                 lleva hoja ni CI de anestesia, y desde el 23/9 el servidor no
                 los pide (`lleva_anestesia: false`). Este «Todo listo» no puede
                 afirmar una hoja firmada que no existe. */
              <p className="text-xs text-muted-foreground mt-1" data-testid="sx-log-parte-listo">
                {requisitos?.lleva_anestesia === false
                  ? 'Todo listo: las dos horas de puerta. Esta intervención no lleva anestesia (marcada «Anestesia local, sin anestesista»), así que no se piden ni la hoja ni el consentimiento de anestesia.'
                  : 'Todo listo: las dos horas de puerta, la hoja de anestesia firmada y el consentimiento de anestesia firmado.'}
              </p>
            ) : faltaParaElParte && faltaParaElParte.length > 0 ? (
              <p className="text-xs text-amber-700 mt-1" data-testid="sx-log-parte-falta">
                Todavía no se puede crear el parte: falta{' '}
                {faltaParaElParte.map((f) => f.texto).join('; ')}.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1" data-testid="sx-log-parte-falta">
                Comprobando con el servidor qué falta para poder crear el parte…
              </p>
            )}
            {/* ANESTESIA DE ESTA INTERVENCIÓN (23/9/2026, DECISIÓN D3 de
                dirección: una cirugía menor lleva, en todo caso, sedación,
                nunca general; diseño cerrado). Por defecto toda intervención
                lleva anestesia. La única forma de que no la lleve es que un
                médico la marque «Anestesia local, sin anestesista», y el
                servidor la rechaza (o la deja sin efecto) si la cita no es de
                cirugía menor, si la hoja tiene anestesista, si hay sedación
                marcada… Aquí se dice lo que conteste, sin deducir nada. */}
            {logId && !parteCreadoAt && requisitos?.anestesia && (
              <div className="mt-2 rounded-md border border-border px-3 py-2 text-xs" data-testid="sx-log-anestesia">
                {requisitos.anestesia.lleva ? (
                  <p data-testid="sx-log-anestesia-motivos">
                    Se piden la hoja y el consentimiento de anestesia:{' '}
                    {(requisitos.anestesia.motivos || []).join('; ')}.
                  </p>
                ) : (
                  <p data-testid="sx-log-anestesia-motivos">
                    No se piden la hoja ni el consentimiento de anestesia: está marcada
                    «Anestesia local, sin anestesista».
                  </p>
                )}
                {requisitos.anestesia.marca && (
                  <p className="mt-1 text-muted-foreground" data-testid="sx-log-anestesia-marca">
                    Marcada «{requisitos.anestesia.marca.valor === 'local'
                      ? 'Anestesia local, sin anestesista' : 'Lleva anestesista'}»
                    {requisitos.anestesia.marca.por_name ? ` por ${requisitos.anestesia.marca.por_name}` : ''}
                    {requisitos.anestesia.marca.at ? ` el ${formatDate(String(requisitos.anestesia.marca.at).slice(0, 10))}` : ''}.
                  </p>
                )}
                {requisitos.anestesia.local_sin_efecto && (
                  <p className="mt-1 text-amber-700" data-testid="sx-log-anestesia-local-sin-efecto">
                    La marca «Anestesia local» ya no vale por lo de arriba, y se vuelve a pedir la anestesia.
                  </p>
                )}
                {errorMarca && (
                  <p className="mt-1 text-red-700" data-testid="sx-log-anestesia-error">{errorMarca}</p>
                )}
                {confirmandoLocal && (
                  <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5" data-testid="sx-log-anestesia-confirmar-local">
                    {/* No se pisa en un clic lo que dijo el anestesista (D3,
                        pasada final): el servidor pide la confirmación
                        expresa, y aquí se pregunta con su nombre. */}
                    {requisitos.anestesia.marca?.valor === 'anestesista' && (
                      <p className="font-semibold text-red-700" data-testid="sx-log-anestesia-sobre-anestesista">
                        {requisitos.anestesia.marca.por_name || 'El anestesista'} marcó que lleva anestesista. ¿Seguro que es local?
                      </p>
                    )}
                    <p className="font-medium">Antes de marcar «Anestesia local, sin anestesista», lo que dice la hoja de anestesia:</p>
                    {Object.keys(requisitos.anestesia.aviso_de_la_hoja || {}).length ? (
                      <ul className="list-disc pl-4" data-testid="sx-log-anestesia-aviso-hoja">
                        {requisitos.anestesia.aviso_de_la_hoja.anestesico && <li>Anestésico: {requisitos.anestesia.aviso_de_la_hoja.anestesico}</li>}
                        {requisitos.anestesia.aviso_de_la_hoja.premedicacion && <li>Premedicación: {requisitos.anestesia.aviso_de_la_hoja.premedicacion}</li>}
                        {requisitos.anestesia.aviso_de_la_hoja.sedacion && <li>Sedación: {requisitos.anestesia.aviso_de_la_hoja.sedacion}</li>}
                      </ul>
                    ) : (
                      <p data-testid="sx-log-anestesia-aviso-hoja">Nada escrito en anestésico, premedicación ni sedación.</p>
                    )}
                    <div className="mt-1.5 flex gap-2">
                      <Button
                        type="button" size="sm" className="h-7 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                        disabled={marcandoAnestesia}
                        onClick={() => marcarAnestesia('local',
                          requisitos.anestesia.marca?.valor === 'anestesista'
                            ? { confirmar_sobre_anestesista: true } : {})}
                        data-testid="sx-log-anestesia-confirmar"
                      >
                        Confirmar «Anestesia local, sin anestesista»
                      </Button>
                      <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setConfirmandoLocal(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
                {(puedeMarcarLocal || puedeMarcarAnestesista) && !confirmandoLocal && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {puedeMarcarAnestesista && (
                      <Button
                        type="button" size="sm" variant="outline" className="h-7 text-xs"
                        disabled={marcandoAnestesia || requisitos.anestesia.marca?.valor === 'anestesista'}
                        onClick={() => marcarAnestesia('anestesista')}
                        data-testid="sx-log-anestesia-lleva"
                      >
                        Lleva anestesista
                      </Button>
                    )}
                    {puedeMarcarLocal && (
                      <Button
                        type="button" size="sm" variant="outline" className="h-7 text-xs"
                        disabled={marcandoAnestesia || requisitos.anestesia.marca?.valor === 'local'}
                        onClick={() => { setErrorMarca(null); setConfirmandoLocal(true); }}
                        data-testid="sx-log-anestesia-local"
                      >
                        Anestesia local, sin anestesista
                      </Button>
                    )}
                    {puedeMarcarLocal && requisitos.anestesia.marca && (
                      <Button
                        type="button" size="sm" variant="ghost" className="h-7 text-xs"
                        disabled={marcandoAnestesia}
                        onClick={() => marcarAnestesia(null)}
                        data-testid="sx-log-anestesia-deshacer"
                      >
                        Quitar la marca
                      </Button>
                    )}
                  </div>
                )}
                {(requisitos.anestesia_historial || []).length > 0 && (
                  <ul className="mt-2 text-muted-foreground" data-testid="sx-log-anestesia-historial">
                    {requisitos.anestesia_historial.map((h, i) => (
                      <li key={`${h.at}-${i}`}>
                        {h.at ? `${formatDate(String(h.at).slice(0, 10))} ${String(h.at).slice(11, 16)}` : ''}
                        {' · '}{h.por_name || 'Alguien'}:{' '}
                        {h.valor === 'local' ? 'marcó «Anestesia local, sin anestesista»'
                          : h.valor === 'anestesista' ? 'marcó «Lleva anestesista»' : 'quitó la marca'}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              {/* «Guardar registro» y no «Crear»: desde hoy hay al lado un
                  «Crear parte» que es otra cosa (cierra la intervención), y
                  dos botones que empiezan por «Crear» en el mismo diálogo se
                  confunden a las ocho de la mañana. */}
              <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="sx-log-save">
                {isEdit ? 'Guardar' : 'Guardar registro'}
              </Button>
            </DialogFooter>
          </div>
        </form>
        {isEdit && pacienteId && (
          <AnesthesiaSheetDialog
            open={firmasOpen}
            onOpenChange={(abierto) => {
              setFirmasOpen(abierto);
              // La firma del CONSENTIMIENTO de anestesia no pasa por
              // `onFirmas` (24/9/2026): al cerrar «Firmas» se vuelve a
              // preguntar qué falta, o «Crear parte» seguía pidiendo una firma
              // que ya estaba puesta, y parecía que no se había guardado.
              if (!abierto) cargarRequisitos(logId);
            }}
            patient={{ patient_id: pacienteId, patient_name: form.patient_name }}
            surgeryDate={(form.date || '').slice(0, 10)}
            surgeryLogId={logId}
            surgeon={form.surgeon}
            onFirmas={(hoja) => {
              const reg = hoja.registro_quirurgico || {};
              setRegistro((r) => ({
                ...(r || {}),
                ...reg,
                firma_anestesista_at: hoja.firmado_at || r?.firma_anestesista_at || null,
              }));
              // La hora que acaba de sellar la firma baja también al
              // FORMULARIO. Sin esto, «Guardar» sin cerrar el diálogo mandaba
              // la hora de antes —normalmente en blanco— y borraba la recién
              // sellada: justo el dato que este encargo existe para capturar,
              // y el que acaba en la factura del cirujano externo
              // (remate 2026-09-17).
              setForm((f) => ({
                ...f,
                hora_inicio: reg.hora_inicio_origen === 'firma_cirujano' ? (reg.hora_inicio || f.hora_inicio) : f.hora_inicio,
                hora_final: reg.hora_final_origen === 'firma_cirujano' ? (reg.hora_final || f.hora_final) : f.hora_final,
              }));
              // Que el listado enseñe ya la hora sellada.
              onSaved && onSaved();
              // Y que «Crear parte» se entere de que ya hay firma: lo que
              // falta lo dice el servidor, así que se le vuelve a preguntar.
              cargarRequisitos(logId);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SurgeryLogPage() {
  const { user } = useAuth();
  const admin = isAdminRole(user);
  // 2026-09-17 — El registro quirúrgico lo lleva mucha gente (quirófano,
  // enfermería, administrativas), pero su Excel lo sirve require_management():
  // a quien no sea dirección/admin el servidor le contesta 403. Se ESCONDE el
  // botón en vez de avisar al pulsarlo: el resto de la pantalla funciona para
  // esa persona y un botón que siempre falla solo hace dudar de si el registro
  // se ha guardado.
  const puedeExportarExcel = isManagement(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Igual que en Cobros: un error tiene que verse como un error y no como
  // un mes sin cirugías.
  const [fallo, setFallo] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [surgeons, setSurgeons] = useState([]);
  const [sheetCounts, setSheetCounts] = useState({});
  // Phase 27.8 — Partes de quirófano: el equipo se define una vez por parte.
  const [partes, setPartes] = useState([]);
  const [parteDialogOpen, setParteDialogOpen] = useState(false);
  const [editingParte, setEditingParte] = useState(null);
  const [parteForLog, setParteForLog] = useState(null); // parte al añadir intervención
  // Phase 24.A2 — Filtros tipo Excel sobre el listado
  const [filterSurgeon, setFilterSurgeon] = useState('all');
  const [filterIntervention, setFilterIntervention] = useState('all');
  const [searchText, setSearchText] = useState('');
  // Phase 27.8 — Filtros adicionales con los desplegables del catálogo
  const [filterAnestesista, setFilterAnestesista] = useState('all');
  const [filterClinica, setFilterClinica] = useState('all');
  const [filterLugar, setFilterLugar] = useState('all');

  // Las cirugías del filtro salen del CATÁLOGO de procedimientos de la
  // clínica (/api/surgeries), no de una lista escrita aquí: se ofrecen las
  // que aparecen en alguna intervención del listado, con el mismo criterio
  // con el que filtra (el texto de la intervención las contiene).
  const [catalogoCirugias, setCatalogoCirugias] = useState([]);
  useEffect(() => {
    api.get('/api/surgeries')
      .then((r) => setCatalogoCirugias(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCatalogoCirugias([]));
  }, []);
  const SURGERY_CATEGORIES = useMemo(() => {
    const textos = (items || []).map((it) => normalizaTexto(it.intervention));
    const nombres = new Set();
    catalogoCirugias.forEach((c) => {
      if (c?.active === false) return;
      const nombre = String(c?.name || '').trim();
      const clave = normalizaTexto(nombre);
      if (clave && textos.some((t) => t.includes(clave))) nombres.add(nombre);
    });
    return [...nombres].sort((a, b) => a.localeCompare(b, 'es'));
  }, [items, catalogoCirugias]);

  // Phase 27.8 — Los filtros usan el MISMO catálogo que los desplegables del
  // parte (/api/qx-catalogo), más cualquier valor presente en los registros
  // del mes (datos antiguos).
  const [catalogAll, setCatalogAll] = useState([]);
  useEffect(() => {
    api.get('/api/qx-catalogo')
      .then((r) => setCatalogAll(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCatalogAll([]));
  }, []);

  /**
   * 2026-09-09, punto 2: en estos desplegables la misma persona salía DOS
   * veces, una con el nombre de ahora y otra con el que se escribió en los
   * registros de entonces (con y sin tratamiento, con otro apellido
   * delante…). Se resuelve cada valor escrito contra su ficha del catálogo
   * —por el nombre actual o por los que tuvo antes (`nombres_anteriores`)— y
   * se enseña UNA sola entrada, la oficial. Los registros no se tocan (punto
   * 6) y al filtrar por la oficial salen también los antiguos. Un valor que
   * no es de nadie del catálogo («Externo», una clínica escrita a mano) se sigue enseñando,
   * para poder buscarlo.
   */
  const canonico = useCallback(
    (tipo, valor) => canonicoDeCatalogo(tipo, valor, catalogAll), [catalogAll]);

  const optionsFor = (tipo, field) => {
    const names = new Set(catalogAll.filter((e) => e.tipo === tipo).map((e) => e.nombre));
    (items || []).forEach((it) => { if (it[field]) names.add(canonico(tipo, it[field])); });
    return [...names].filter(Boolean).sort((a, b) => a.localeCompare(b, 'es'));
  };

  const SURGEON_OPTIONS = useMemo(() => optionsFor('cirujano', 'surgeon'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogAll, items, canonico]);
  const ANESTESISTA_OPTIONS = useMemo(() => optionsFor('anestesista', 'anesthesiologist'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogAll, items, canonico]);
  const CLINICA_OPTIONS = useMemo(() => optionsFor('clinica_origen', 'origin_clinic'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogAll, items, canonico]);
  const LUGAR_OPTIONS = useMemo(() => optionsFor('lugar', 'surgery_location'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogAll, items, canonico]);

  // Filtrado aplicado en el cliente (lista pequeña, mes a mes).
  const filteredItems = useMemo(() => {
    let arr = items;
    if (filterSurgeon !== 'all') {
      // Por el nombre oficial salen también los registros antiguos («Dr.
      // Nombre» ⊂ «Dr. Nombre Apellido1 Apellido2»); el `includes` se queda
      // para buscar por un trozo del nombre.
      arr = arr.filter((it) => canonico('cirujano', it.surgeon) === filterSurgeon
        || normalizaTexto(it.surgeon).includes(normalizaTexto(filterSurgeon)));
    }
    if (filterIntervention !== 'all') {
      arr = arr.filter((it) => normalizaTexto(it.intervention).includes(normalizaTexto(filterIntervention)));
    }
    if (filterAnestesista !== 'all') {
      arr = arr.filter((it) => canonico('anestesista', it.anesthesiologist) === filterAnestesista);
    }
    if (filterClinica !== 'all') {
      arr = arr.filter((it) => canonico('clinica_origen', it.origin_clinic) === filterClinica);
    }
    if (filterLugar !== 'all') {
      arr = arr.filter((it) => canonico('lugar', it.surgery_location) === filterLugar);
    }
    if (searchText.trim()) {
      const q = normalizaTexto(searchText);
      arr = arr.filter((it) =>
        normalizaTexto(it.patient_name).includes(q) ||
        normalizaTexto(it.intervention).includes(q)
      );
    }
    return arr;
  }, [items, filterSurgeon, filterIntervention, filterAnestesista, filterClinica, filterLugar, searchText, canonico]);

  // Phase 27.9 — Recuento (solo admin): días de trabajo e intervenciones por
  // cirujano y por clínica de origen, sobre las filas visibles (respeta los
  // filtros de arriba: mes, cirujano, clínica, lugar, anestesista…).
  const resumen = useMemo(() => {
    const acc = (keyFn) => {
      const map = new Map();
      for (const it of filteredItems) {
        const key = keyFn(it) || '(sin indicar)';
        if (!map.has(key)) map.set(key, { dias: new Set(), intervenciones: 0 });
        const e = map.get(key);
        if (it.date) e.dias.add(String(it.date).slice(0, 10));
        e.intervenciones += 1;
      }
      return [...map.entries()]
        .map(([nombre, e]) => ({ nombre, dias: e.dias.size, intervenciones: e.intervenciones }))
        .sort((a, b) => b.dias - a.dias || b.intervenciones - a.intervenciones);
    };
    return {
      porCirujano: acc((it) => canonico('cirujano', it.surgeon)),
      porClinica: acc((it) => canonico('clinica_origen', it.origin_clinic)),
      totalDias: new Set(filteredItems.filter((it) => it.date).map((it) => String(it.date).slice(0, 10))).size,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, canonico]);

  const fetchPartes = async () => {
    try {
      const r = await api.get('/api/qx-partes', { params: month ? { month } : {} });
      setPartes(Array.isArray(r.data) ? r.data : []);
    } catch {
      setPartes([]);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    fetchPartes();
    try {
      const r = await api.get('/api/surgery-log', { params: month ? { month } : {} });
      setItems(r.data);
      const counts = {};
      await Promise.all((r.data || []).map(async (it) => {
        try {
          const sr = await api.get('/api/anesthesia-sheets', { params: { surgery_log_id: it.id } });
          counts[it.id] = (sr.data || []).length;
        } catch { counts[it.id] = 0; }
      }));
      setSheetCounts(counts);
      setFallo(null);
    } catch (err) {
      // Sin catch, un corte de red dejaba «Sin registros — Añade la primera
      // cirugía» y se podía volver a apuntar una intervención que ya estaba
      // (2026-09-17). Un error no puede leerse como un dato.
      setItems([]);
      setFallo(err?.response?.status === 403
        ? 'No tienes permiso para ver el registro de quirófano.'
        : 'No se ha podido cargar el registro. Esto NO significa que esté vacío: vuelve a intentarlo antes de apuntar nada.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get('/api/surgeons').then(r => setSurgeons(r.data || [])).catch(() => setSurgeons([]));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchItems(); }, [month]);

  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    await api.delete(`/api/surgery-log/${id}`);
    toast.success('Eliminado');
    fetchItems();
  };

  const exportExcel = async () => {
    const ok = await downloadAuth(`${BACKEND_URL}/api/surgery-log/export-excel?month=${month}`, `registro_qx_${month}.xlsx`);
    // El «descargado» solo se canta si el fichero ha llegado de verdad.
    if (ok) toast.success('Excel descargado');
  };

  /**
   * La hora, con una pluma al lado si la selló la firma del cirujano.
   *
   * Y si alguien la corrigió a mano encima de una firmada, se ve lo que decía
   * la firma: sin eso, nadie podía enterarse de que la firma decía otra cosa
   * —el dato estaba guardado pero no lo pintaba ninguna pantalla— y esa hora
   * es la que se factura al cirujano externo (segundo remate, 2026-09-18).
   */
  const horaConFirma = (hora, origen, firmada) => {
    if (firmada && origen !== 'firma_cirujano') {
      return (
        <span
          className="inline-flex flex-col leading-tight"
          title={`Corregida a mano; la firma del cirujano decía ${firmada}`}
          data-testid="sx-log-hora-corregida"
        >
          <span>{hora || '—'}</span>
          <span className="text-[10px] text-amber-700">la firma decía {firmada}</span>
        </span>
      );
    }
    if (!hora) return '—';
    if (origen !== 'firma_cirujano') return hora;
    return (
      <span className="inline-flex items-center gap-1 text-[#13231E]" title="Hora sellada por la firma del cirujano" data-testid="sx-log-hora-firmada">
        <PenLine className="w-3 h-3 text-[#2E6E5E]" />{hora}
      </span>
    );
  };

  // Zona CO2 descriptiva en la tabla.
  const co2Zone = (it) => {
    if (!it.co2) return '—';
    return it.full_face ? 'Full face' : 'Periocular';
  };

  return (
    <div>
      <PageHeader title="Registro quirúrgico diario" subtitle={`${filteredItems.length} de ${items.length} cirugías en ${month}`}
        actions={<>
          {puedeExportarExcel && (
            <Button onClick={exportExcel} variant="outline" data-testid="sx-log-export"><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel</Button>
          )}
          <Button onClick={() => { setEditingParte(null); setParteDialogOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="qx-nuevo-parte"><Plus className="w-4 h-4 mr-2" /> Nuevo parte</Button>
        </>} />

      {/* Phase 27.8 — Partes de quirófano del mes: el equipo (cirujano,
          anestesista, clínica, lugar, instrumentista, circulante) se define
          una vez por parte y sus intervenciones lo heredan. */}
      <div className="mb-4">
        <div className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Partes de quirófano · {month}
        </div>
        {partes.length === 0 ? (
          <div className="cp-card p-4 text-sm text-muted-foreground">
            Sin partes este mes. Crea uno con «Nuevo parte» y añade dentro sus intervenciones.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {partes.map((p) => (
              <div key={p.id} className="cp-card p-4" data-testid="qx-parte-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-[#13231E] truncate">
                      {formatDate(p.date)} · {p.cirujano}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                      {/* Cirujano externo (2026-09-08, punto 6): sus
                          intervenciones salen en el Registro de externos. */}
                      {p.cirujano_externo && <span className="px-1.5 py-0.5 rounded bg-slate-700 text-white" data-testid="qx-parte-externo">Externo{p.cirujano_otro ? ' (a mano)' : ''}</span>}
                      {p.clinica_origen && <span className="px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.10)] text-[#13231E]">{p.clinica_origen}</span>}
                      {p.lugar && <span className="px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.06)] text-[#13231E]">{p.lugar}</span>}
                      {p.anestesista
                        ? <span className="px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.06)] text-[#13231E]">Anest.: {p.anestesista}</span>
                        : <span className="px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.06)] text-muted-foreground">Sin anestesista</span>}
                      {p.instrumentista && <span className="px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.06)] text-[#13231E]">Instr.: {p.instrumentista}</span>}
                      {p.circulante && <span className="px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.06)] text-[#13231E]">Circ.: {p.circulante}</span>}
                    </div>
                    {p.notas && <div className="mt-1 text-xs text-muted-foreground italic truncate">{p.notas}</div>}
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {p.num_intervenciones || 0} interv.
                  </span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => { setEditing(null); setParteForLog(p); setOpen(true); }}
                    className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                    data-testid="qx-parte-add-intervencion"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Añadir intervención
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingParte(p); setParteDialogOpen(true); }}
                    data-testid="qx-parte-edit"
                  >
                    <Edit className="w-3.5 h-3.5 mr-1" /> Equipo
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="cp-card p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <Label>Mes</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="sx-log-filter-month" />
        </div>
        <div>
          <Label>Cirujano</Label>
          <Select value={filterSurgeon} onValueChange={setFilterSurgeon}>
            <SelectTrigger data-testid="sx-log-filter-surgeon">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {SURGEON_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Cirugía</Label>
          <Select value={filterIntervention} onValueChange={setFilterIntervention}>
            <SelectTrigger data-testid="sx-log-filter-intervention">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {SURGERY_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Anestesista</Label>
          <Select value={filterAnestesista} onValueChange={setFilterAnestesista}>
            <SelectTrigger data-testid="sx-log-filter-anestesista">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {ANESTESISTA_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Clínica de origen</Label>
          <Select value={filterClinica} onValueChange={setFilterClinica}>
            <SelectTrigger data-testid="sx-log-filter-clinica">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {CLINICA_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Lugar</Label>
          <Select value={filterLugar} onValueChange={setFilterLugar}>
            <SelectTrigger data-testid="sx-log-filter-lugar">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {LUGAR_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Buscar paciente / texto</Label>
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Nombre o palabra clave…"
            data-testid="sx-log-filter-search"
          />
        </div>
        {(filterSurgeon !== 'all' || filterIntervention !== 'all' || filterAnestesista !== 'all' || filterClinica !== 'all' || filterLugar !== 'all' || searchText) && (
          <div className="md:col-span-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterSurgeon('all'); setFilterIntervention('all');
                setFilterAnestesista('all'); setFilterClinica('all'); setFilterLugar('all');
                setSearchText('');
              }}
              data-testid="sx-log-filter-clear"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Limpiar filtros
            </Button>
          </div>
        )}
      </div>
      {/* Phase 27.9 — Recuento de días de trabajo (SOLO administradores).
          Usa las filas visibles: combina los filtros de arriba para sacar,
          p. ej., los días de una clínica este mes o los de un cirujano. */}
      {admin && filteredItems.length > 0 && (
        <div className="cp-card p-4 mb-4" data-testid="qx-resumen">
          <div className="flex items-baseline justify-between gap-2 mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Recuento (según filtros) · solo administradores
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold text-[#13231E] tabular-nums">{resumen.totalDias}</span> días ·{' '}
              <span className="font-semibold text-[#13231E] tabular-nums">{filteredItems.length}</span> intervenciones
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-[#13231E] mb-1.5">Por cirujano</div>
              <div className="divide-y divide-border">
                {resumen.porCirujano.map((r) => (
                  <div key={r.nombre} className="flex items-center justify-between py-1.5 text-sm" data-testid="qx-resumen-cirujano">
                    <span className="truncate">{r.nombre}</span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      <span className="font-semibold text-[#13231E]">{r.dias}</span> {r.dias === 1 ? 'día' : 'días'} · {r.intervenciones} interv.
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-[#13231E] mb-1.5">Por clínica de origen</div>
              <div className="divide-y divide-border">
                {resumen.porClinica.map((r) => (
                  <div key={r.nombre} className="flex items-center justify-between py-1.5 text-sm" data-testid="qx-resumen-clinica">
                    <span className="truncate">{r.nombre}</span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      <span className="font-semibold text-[#13231E]">{r.dias}</span> {r.dias === 1 ? 'día' : 'días'} · {r.intervenciones} interv.
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="cp-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : fallo ? (
            <div className="py-8 px-4 text-center" data-testid="surgery-log-fallo">
              <div className="text-sm font-medium text-[#13231E]">No se ha podido cargar el registro</div>
              <div className="text-sm text-muted-foreground mt-1">{fallo}</div>
            </div>
          )
          : filteredItems.length === 0 ? <EmptyState icon={ClipboardList} title="Sin registros" description={items.length === 0 ? 'Añade la primera cirugía.' : 'Ningún registro coincide con los filtros.'} />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left">
              <tr>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Fecha</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Intervención</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">CO2</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Cirujano</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Anestesia</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Sedación</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Origen</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Lugar</th>
                {/* Cuatro horas, cuatro nombres. Las de la puerta miden la
                    ocupación de sala; las de la cirugía son las de las firmas
                    y son las que se facturan (19/9/2026). */}
                <th className="py-3 px-4 font-semibold text-[#13231E]" title="El paciente entra por la puerta del quirófano">Entrada sala</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]" title="El paciente sale por la puerta del quirófano">Salida sala</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]" title="Lo sella la firma del cirujano; es la hora que se factura">Inicio cirugía</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]" title="Lo sella la firma del cirujano; es la hora que se factura">Fin cirugía</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">H.A.</th>
                <th></th>
              </tr>
            </thead>
            <tbody>{filteredItems.map(it => <tr key={it.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="sx-log-row">
              <td className="py-3 px-4 text-xs">{formatDate(it.date)}</td>
              <td className="py-3 px-4 font-medium">{it.patient_name}</td>
              <td className="py-3 px-4">{it.intervention}</td>
              <td className="py-3 px-4 text-xs">{co2Zone(it)}</td>
              <td className="py-3 px-4 text-xs">{it.surgeon}</td>
              <td className="py-3 px-4 text-xs">{it.anesthesiologist || '—'}</td>
              <td className="py-3 px-4 text-xs">{it.sedacion === true ? 'Sí' : it.sedacion === false ? 'No' : '—'}</td>
              <td className="py-3 px-4 text-xs">{it.origin_clinic || '—'}</td>
              <td className="py-3 px-4 text-xs">{it.surgery_location || '—'}</td>
              {/* Las de la PUERTA: sin pluma de firma y en gris, porque no son
                  las de la cirugía ni se facturan. */}
              <td className="py-3 px-4 text-xs text-muted-foreground" data-testid="sx-log-entrada-sala">{it.hora_entrada_quirofano || '—'}</td>
              <td className="py-3 px-4 text-xs text-muted-foreground" data-testid="sx-log-salida-sala">{it.hora_salida_quirofano || '—'}</td>
              {/* La hora que viene de una firma del cirujano se distingue de
                  la apuntada a mano: es justo lo que se quería medir. */}
              <td className="py-3 px-4 text-xs">{horaConFirma(it.hora_inicio, it.hora_inicio_origen, it.hora_inicio_firmada)}</td>
              <td className="py-3 px-4 text-xs">{horaConFirma(it.hora_final, it.hora_final_origen, it.hora_final_firmada)}</td>
              <td className="py-3 px-4 text-xs text-center" title="Hojas de Anestesia adjuntas">
                {sheetCounts[it.id] > 0
                  ? <span className="inline-flex items-center gap-1 text-[#13231E]"><Paperclip className="w-3 h-3" />{sheetCounts[it.id]}</span>
                  : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="py-3 px-4 text-right"><div className="flex gap-1 items-center justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(it); setOpen(true); }} data-testid="sx-log-edit"><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(it.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div></td>
            </tr>)}</tbody>
          </table></div>}
      </div>
      <LogDialog
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setParteForLog(null); }}
        initial={editing}
        onSaved={fetchItems}
        surgeons={surgeons}
        parte={parteForLog || (editing?.parte_id ? partes.find((p) => p.id === editing.parte_id) || null : null)}
      />
      <ParteDialog
        open={parteDialogOpen}
        onOpenChange={setParteDialogOpen}
        initial={editingParte}
        onSaved={fetchItems}
      />
    </div>
  );
}
